// The cross-link glossary: every name in the setting that should become an
// auto-link inside lore markdown, mapped to its entry page (or a deep anchor)
// and a one-line tagline shown on hover.
//
// Collected from the typed lore model so adding a new body/species/plant/faction
// makes it linkable everywhere automatically. A few hand-curated aliases (named
// figures, products, the system itself) are appended at the bottom.

import { system404, allBodies, allFactions, lorebookHref } from './index'

export interface GlossaryTerm {
  /** exact, case-sensitive text to find in prose */
  match: string
  /** internal path (may include a #anchor) */
  href: string
  /** one-line hover tip; omitted entries still link but show no bubble */
  tip?: string
}

export interface Glossary {
  /** terms sorted longest-first so multi-word names win over sub-words */
  terms: GlossaryTerm[]
  /** lookup by exact match string */
  byTerm: Map<string, GlossaryTerm>
  /** single combined regex with unicode word boundaries + optional plural 's' */
  regex: RegExp
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

let cached: Glossary | null = null

/** Build (once) and return the glossary. */
export function getGlossary(): Glossary {
  if (cached) return cached

  const terms: GlossaryTerm[] = []

  // bodies (star, planets, moons) -> their own pages
  for (const b of allBodies) {
    terms.push({ match: b.name, href: lorebookHref(b), tip: b.tagline })
  }

  // species & plants -> deep anchors inside their parent body's page
  for (const b of allBodies) {
    for (const s of b.species ?? []) {
      terms.push({ match: s.name, href: `/lorebook/${b.id}#fauna-${s.id}`, tip: s.tagline })
    }
    for (const p of b.plants ?? []) {
      terms.push({ match: p.name, href: `/lorebook/${b.id}#plant-${p.id}`, tip: p.tagline })
    }
  }

  // factions (parents + subfactions) -> their own pages
  for (const f of allFactions) {
    terms.push({ match: f.name, href: `/lorebook/${f.id}`, tip: f.tagline })
  }

  // hand-curated aliases: named figures, products, short forms, the system.
  terms.push(
    { match: 'System 404', href: '/lorebook', tip: system404.tagline },
    { match: 'The Lie', href: '/lorebook#anomalies', tip: 'What insiders call System 404.' },
    {
      match: 'Ysildea Yllawraek',
      href: '/lorebook/helix-syndicate#leadership',
      tip: 'Leader of the Helix Syndicate.',
    },
    {
      match: 'Ysildea',
      href: '/lorebook/helix-syndicate#leadership',
      tip: 'Leader of the Helix Syndicate.',
    },
    {
      match: 'Aen Marath',
      href: '/lorebook/helix-syndicate#seat',
      tip: 'Citadel-capital of the Helix Syndicate, in the northern plains of Verrithis.',
    },
    { match: 'Helix Syndicate', href: '/lorebook/helix-syndicate', tip: helixTip() },
    { match: 'Tethered', href: '/lorebook/the-tethered', tip: tetheredTip() },
    { match: 'Rooted', href: '/lorebook/the-rooted', tip: rootedTip() },
    {
      match: 'Gloamsap',
      href: '/lorebook/verrithis#plant-gloamsap-yew',
      tip: 'Refined resin, the most valuable trade good in the system.',
    },
    {
      match: 'mournwhisper',
      href: '/lorebook/verrithis#fauna-mournwhisper',
      tip: 'Officially does not exist. Connected to the anomaly?',
    },
  )

  // dedupe by match (last wins, so curated tips can override auto tips)
  const byTerm = new Map<string, GlossaryTerm>()
  for (const t of terms) if (t.match) byTerm.set(t.match, t)

  const sorted = [...byTerm.values()].sort((a, b) => b.match.length - a.match.length)
  const pattern = sorted.map((t) => escapeRegex(t.match)).join('|')
  // lookbehind/ahead on unicode letters/digits keeps matches whole-word;
  // an optional trailing 's' is captured so plurals ("Brumbles") still link.
  const regex = new RegExp(`(?<![\\p{L}\\p{N}])((?:${pattern})(?:s)?)(?![\\p{L}\\p{N}])`, 'gu')

  cached = { terms: sorted, byTerm, regex }
  return cached
}

/** Resolve a matched span to its glossary term, tolerating a plural 's'. */
export function findTerm(text: string): GlossaryTerm | undefined {
  const g = getGlossary()
  return g.byTerm.get(text) ?? (text.endsWith('s') ? g.byTerm.get(text.slice(0, -1)) : undefined)
}

// tagline helpers for short-form faction aliases (kept terse for tooltips)
function helixTip(): string {
  return system404.factions.find((f) => f.id === 'helix-syndicate')?.tagline ?? 'They came to solve it. Now they run it.'
}
function tetheredTip(): string {
  return system404.factions.find((f) => f.id === 'the-tethered')?.tagline ?? 'Independent ships orbiting Verrithis.'
}
function rootedTip(): string {
  return system404.factions.find((f) => f.id === 'the-rooted')?.tagline ?? 'Settlers who chose to live, not leave.'
}
